import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { colors, typography, spacing, borderRadius, animation, sizing } from '../../theme';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ id, message, variant, duration = 5000, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, onClose]);

  const handleClose = () => {
    onClose(id);
  };

  const getIcon = () => {
    switch (variant) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return 'ℹ️';
    }
  };

  return (
    <ToastContainer variant={variant}>
      <ToastIcon>{getIcon()}</ToastIcon>
      <ToastMessage>{message}</ToastMessage>
      <CloseButton onClick={handleClose}>✕</CloseButton>
    </ToastContainer>
  );
};

export default Toast;

const ToastContainer = styled.div<{ variant: ToastVariant }>`
  display: flex;
  align-items: center;
  gap: ${spacing.md}px;
  min-width: 300px;
  max-width: 500px;
  padding: ${spacing.md}px ${spacing.lg}px;
  background: ${({ variant }) => {
    switch (variant) {
      case 'success':
        return 'var(--success-bg)';
      case 'error':
        return 'var(--danger-bg)';
      case 'warning':
        return 'var(--warning-bg)';
      case 'info':
        return 'var(--accent-subtle)';
      default:
        return 'var(--bg-elevated)';
    }
  }};
  border: 1px solid ${({ variant }) => {
    switch (variant) {
      case 'success':
        return colors.truthGreen;
      case 'error':
        return colors.cautionRed;
      case 'warning':
        return colors.insightOrange;
      case 'info':
        return 'var(--accent-border)';
      default:
        return 'var(--border-default)';
    }
  }};
  border-radius: ${borderRadius.md}px;
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-md);
  animation: slideInRight ${animation.duration.normal} ${animation.easing.easeOut}, fadeOut ${animation.duration.normal} ${animation.easing.easeIn} ${props => (props.variant === 'success' || props.variant === 'info') ? '4.7s' : '4.7s'} forwards;

  @keyframes slideInRight {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
`;

const ToastIcon = styled.span`
  font-size: ${typography.fontSize.xl};
  flex-shrink: 0;
`;

const ToastMessage = styled.p`
  flex: 1;
  margin: 0;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.medium};
  color: var(--fg-primary);
  line-height: ${typography.lineHeight.normal};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--fg-primary);
  font-size: ${typography.fontSize.lg};
  cursor: pointer;
  padding: 0;
  width: ${sizing.icon.md}px;
  height: ${sizing.icon.md}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.xs}px;
  transition: all ${animation.duration.fast} ${animation.easing.default};
  flex-shrink: 0;

  &:hover {
    background: var(--bg-hover);
  }

  &:active {
    background: var(--bg-active);
  }
`;
